// packages/web/src/components/fallbacks/FeatureDisabled.tsx
// Fallback components for graceful degradation

import { ErrorOutline as ErrorIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Box, Typography, Alert, AlertTitle, Button } from '@mui/material';
import React from 'react';

interface FeatureDisabledProps {
  feature: string;
  title?: string;
  message?: string;
  showRetry?: boolean;
  onRetry?: () => void;
  children?: React.ReactNode;
}

export function FeatureDisabled({
  feature,
  title,
  message,
  showRetry = true,
  onRetry,
  children
}: FeatureDisabledProps) {
  const defaultTitle = `${feature.charAt(0).toUpperCase() + feature.slice(1)} Unavailable`;
  const defaultMessage = `The ${feature} feature is currently unavailable. Please try again later.`;

  return (
    <Box
      sx={{
        p: 3,
        textAlign: 'center',
        border: '1px solid',
        borderColor: 'warning.main',
        borderRadius: 2,
        backgroundColor: 'warning.light',
        opacity: 0.8
      }}
    >
      <ErrorIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
      <Typography variant="h6" gutterBottom color="warning.dark">
        {title || defaultTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {message || defaultMessage}
      </Typography>

      {showRetry && onRetry && (
        <Button
          variant="outlined"
          color="warning"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          sx={{ mr: 1 }}
        >
          Try Again
        </Button>
      )}

      {children}
    </Box>
  );
}

interface CartDisabledProps {
  onRetry?: () => void;
}

export function CartDisabled({ onRetry }: CartDisabledProps) {
  return (
    <FeatureDisabled
      feature="cart"
      title="Shopping Cart Unavailable"
      message="We're unable to load your shopping cart right now. Your selections are saved and will be available when the service is restored."
      onRetry={onRetry}
    >
      <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
        <AlertTitle>What you can do:</AlertTitle>
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>Browse services normally</li>
          <li>Your cart will be restored when available</li>
          <li>Contact support if you need immediate assistance</li>
        </ul>
      </Alert>
    </FeatureDisabled>
  );
}

interface AddressesDisabledProps {
  onRetry?: () => void;
}

export function AddressesDisabled({ onRetry }: AddressesDisabledProps) {
  return (
    <FeatureDisabled
      feature="addresses"
      title="Address Management Unavailable"
      message="We're unable to load your saved addresses. You can still browse services, but address selection may be limited."
      onRetry={onRetry}
    />
  );
}

interface MarketplaceDisabledProps {
  onRetry?: () => void;
}

export function MarketplaceDisabled({ onRetry }: MarketplaceDisabledProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3,
        textAlign: 'center'
      }}
    >
      <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 3 }} />
      <Typography variant="h4" component="h1" gutterBottom color="error">
        Marketplace Temporarily Unavailable
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600 }}>
        We're experiencing technical difficulties with our marketplace service.
        Our team has been notified and is working to restore full functionality.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
        {onRetry && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
          >
            Try Again
          </Button>
        )}
        <Button
          variant="outlined"
          size="large"
          onClick={() => window.location.href = '/dashboard'}
        >
          Go to Dashboard
        </Button>
      </Box>

      <Alert severity="info" sx={{ mt: 4, maxWidth: 600, textAlign: 'left' }}>
        <AlertTitle>Status Information:</AlertTitle>
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>Service browsing is temporarily disabled</li>
          <li>Existing orders are unaffected</li>
          <li>Account management remains available</li>
          <li>We'll send updates when service is restored</li>
        </ul>
      </Alert>
    </Box>
  );
}

interface ApiErrorFallbackProps {
  error?: string;
  onRetry?: () => void;
}

export function ApiErrorFallback({ error, onRetry }: ApiErrorFallbackProps) {
  return (
    <Alert severity="error" sx={{ m: 2 }}>
      <AlertTitle>Connection Error</AlertTitle>
      <Typography variant="body2">
        {error || 'Unable to connect to our servers. Please check your internet connection and try again.'}
      </Typography>
      {onRetry && (
        <Button
          size="small"
          color="error"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          sx={{ mt: 1 }}
        >
          Retry
        </Button>
      )}
    </Alert>
  );
}

// Skeleton loader for graceful loading states
export function FeatureSkeleton({ height = 200 }: { height?: number }) {
  return (
    <Box
      sx={{
        height,
        backgroundColor: 'grey.100',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Loading...
      </Typography>
    </Box>
  );
}